import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useOrgAuth } from '../../contexts/OrgAuthContext'
import { useNotifications } from '../../hooks/useNotifications'
import NotificationPanel from './NotificationPanel'

/**
 * <NotificationsBell> — the notification bell + panel, self-contained.
 *
 * Renders the bell and the panel in one `relative` wrapper so the panel can be
 * `fixed inset-0` (full-screen) on mobile and `sm:absolute sm:right-0 sm:top-full`
 * (anchored dropdown) on desktop — the codebase's standard responsive-overlay
 * pattern. (Radix `Popover` was tried but its content is wrapped in a transformed
 * anchor layer, which can't be made cleanly full-screen on mobile; this inline
 * approach is the right fit here. The `Popover` primitive is kept for future
 * anchored-only dropdowns.)
 *
 * Both `AppHeader` and `AppLayout` render this, so the bell logic lives in one place.
 */
export default function NotificationsBell() {
  const { t } = useTranslation()
  const location = useLocation()
  const [panelOpen, setPanelOpen] = useState(false)
  const { session } = useOrgAuth()
  const {
    notifications,
    unreadCount,
    loading: notifLoading,
    hasMore,
    markRead,
    markAllRead,
    archiveAll,
    loadMore,
    loadPage,
  } = useNotifications()

  // The full inbox lives in the procurement module ("one inbox, two windows"),
  // so the link is only offered to users who can actually open that module.
  const canOpenInbox = (session?.permissions ?? []).includes('module.procurements.view')

  // Close the panel whenever the route changes.
  useEffect(() => {
    setPanelOpen(false)
  }, [location.pathname])

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          if (!panelOpen) loadPage(1)
          setPanelOpen((p) => !p)
        }}
        className="relative flex items-center justify-center p-2 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-600 transition-colors"
        title={t('notifications.title', 'Notifications')}
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[1rem] h-4 flex items-center justify-center rounded-full bg-primary-600 text-white text-[9px] font-semibold px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      <NotificationPanel
        isOpen={panelOpen}
        onClose={() => setPanelOpen(false)}
        notifications={notifications}
        unreadCount={unreadCount}
        loading={notifLoading}
        hasMore={hasMore}
        seeAllTo={canOpenInbox ? '/app/procurement' : undefined}
        markRead={markRead}
        markAllRead={markAllRead}
        archiveAll={archiveAll}
        loadMore={loadMore}
      />
    </div>
  )
}
